/**
 * Sprint 1 UI additions for the Candidate Detail page:
 *  - FollowupQuestionsCard: click "Generate" → gets 3 STAR-style follow-up questions
 *  - FraudCheckCard: shows IP overlap flags (critical/warning), lets recruiter resolve them
 *  - ClientSharesCard: track which clients this candidate has been "floated to" + verdicts
 *
 * Sprint 2 MOCK:
 *  - OutreachDialog: send an SMS/WhatsApp with a clearly labelled MOCKED banner
 */
import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, ShieldAlert, ShieldCheck, Share2, MessageCircle, Trash2, ThumbsUp, ThumbsDown, HelpCircle, Info, Phone, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";

// ==================== Follow-up questions ====================

export function FollowupQuestionsCard({ candidateId }) {
  const [data, setData] = useState({ questions: [], generated_at: null });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!candidateId) return;
    api.get(`/recruit/candidates/${candidateId}/followup-questions`).then(r => setData(r.data)).catch(() => {});
  }, [candidateId]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data: d } = await api.post(`/recruit/candidates/${candidateId}/followup-questions`);
      setData(d);
      toast.success("Follow-up questions ready");
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="border-0 shadow-sm" data-testid="followup-questions-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-pink" />
            <h3 className="font-semibold">1:1 Follow-up Questions</h3>
            <Badge variant="outline" className="text-[9px] uppercase tracking-widest">AI · Tailored</Badge>
          </div>
          <Button size="sm" onClick={generate} disabled={busy} className="btn-primary rounded-full" data-testid="gen-followups-btn">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> {busy ? "Generating…" : (data.questions?.length ? "Regenerate" : "Generate")}
          </Button>
        </div>
        {data.questions?.length ? (
          <ol className="space-y-2 text-sm">
            {data.questions.map((q, i) => (
              <li key={i} className="flex gap-3" data-testid={`followup-q-${i}`}>
                <span className="w-6 h-6 shrink-0 rounded-full bg-pink/10 text-pink grid place-items-center text-xs font-bold">{i + 1}</span>
                <span className="text-neutral-700 leading-relaxed">{q}</span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-neutral-500 italic">Tap Generate to get 3 STAR-style follow-up questions tailored to what this candidate said in their video and wrote in their resume.</p>
        )}
      </CardContent>
    </Card>
  );
}

// ==================== Fraud check ====================

export function FraudCheckCard({ candidateId }) {
  const [d, setD] = useState({ candidate: {}, flags: [] });
  const load = () => api.get(`/recruit/candidates/${candidateId}/fraud`).then(r => setD(r.data)).catch(() => {});
  useEffect(() => { if (candidateId) load(); }, [candidateId]);

  const openFlags = (d.flags || []).filter(f => !f.resolved_at);
  const status = d.candidate?.fraud_status;

  const resolve = async (fraud_id) => {
    try { await api.post(`/recruit/candidates/${candidateId}/fraud/${fraud_id}/resolve`); toast.success("Marked resolved"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <Card className={`border-0 shadow-sm ${status === "critical" ? "ring-2 ring-red-400" : ""}`} data-testid="fraud-check-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-3">
          {openFlags.length ? <ShieldAlert className="w-4 h-4 text-red-500" /> : <ShieldCheck className="w-4 h-4 text-teal" />}
          <h3 className="font-semibold">Anti-Fraud Check</h3>
          {status === "critical" && <Badge className="bg-red-100 text-red-700 border border-red-200 text-[9px] uppercase tracking-widest">Critical</Badge>}
          {status === "warning" && <Badge className="bg-amber-100 text-amber-700 border border-amber-200 text-[9px] uppercase tracking-widest">Warning</Badge>}
          {!status && <Badge className="bg-teal/10 text-teal border border-teal/30 text-[9px] uppercase tracking-widest">Clean</Badge>}
        </div>
        <div className="text-xs text-neutral-500 mb-3">
          Submission IP: <code className="bg-neutral-100 px-1.5 py-0.5 rounded font-mono text-neutral-700">{d.candidate?.submission_ip || "—"}</code>
        </div>
        {openFlags.length ? (
          <div className="space-y-2">
            {openFlags.map((f, i) => (
              <div key={i} className="border border-red-200 bg-red-50 rounded-lg p-3 space-y-2" data-testid={`fraud-flag-${i}`}>
                {(f.flags || []).map((x, j) => (
                  <div key={j} className="text-xs text-red-900 flex items-start gap-2">
                    <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span><strong className="uppercase tracking-wider text-[10px]">{x.type}:</strong> {x.message}</span>
                  </div>
                ))}
                <div className="flex justify-end">
                  <Button size="sm" variant="outline" onClick={() => resolve(f.fraud_id)} data-testid={`resolve-fraud-${i}`}>Mark resolved</Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-neutral-500 italic">No IP overlaps detected between this candidate, their referees, or other candidates.</p>
        )}
      </CardContent>
    </Card>
  );
}

// ==================== Client shares tracker ====================

const VERDICT_STYLES = {
  INTERESTED: { Icon: ThumbsUp, cls: "bg-teal/10 text-teal border-teal/30" },
  PASS: { Icon: ThumbsDown, cls: "bg-red-100 text-red-700 border-red-200" },
  MAYBE: { Icon: HelpCircle, cls: "bg-amber-100 text-amber-700 border-amber-200" },
};

export function ClientSharesCard({ candidateId, candidateName }) {
  const [shares, setShares] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_name: "", client_email: "", client_company: "", notes: "", channel: "email" });

  const load = () => api.get(`/recruit/candidate-shares?candidate_id=${candidateId}`).then(r => setShares(r.data)).catch(() => {});
  useEffect(() => { if (candidateId) load(); }, [candidateId]);

  const submit = async () => {
    if (!form.client_name.trim()) { toast.error("Client name required"); return; }
    try {
      await api.post("/recruit/candidate-shares", { candidate_id: candidateId, ...form });
      toast.success(`Floated ${candidateName} to ${form.client_name}`);
      setOpen(false);
      setForm({ client_name: "", client_email: "", client_company: "", notes: "", channel: "email" });
      load();
    } catch { toast.error("Share failed"); }
  };
  const setFeedback = async (share_id, verdict) => {
    try { await api.patch(`/recruit/candidate-shares/${share_id}/feedback`, { verdict }); toast.success("Feedback recorded"); load(); }
    catch { toast.error("Failed"); }
  };
  const del = async (share_id) => {
    if (!confirm("Delete this share record?")) return;
    try { await api.delete(`/recruit/candidate-shares/${share_id}`); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <Card className="border-0 shadow-sm" data-testid="client-shares-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Share2 className="w-4 h-4 text-pink" />
            <h3 className="font-semibold">Floated to clients</h3>
            <Badge variant="outline" className="text-[9px] uppercase tracking-widest">{shares.length}</Badge>
          </div>
          <div className="flex gap-2">
            <OutreachDialog candidateId={candidateId} candidateName={candidateName} />
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="btn-primary rounded-full" data-testid="add-share-btn"><Share2 className="w-3.5 h-3.5 mr-1" /> Float to client</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Float {candidateName} to a client</DialogTitle>
                  <DialogDescription>Record who you've shared this candidate with so you can track feedback later.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div><Label>Client name *</Label><Input value={form.client_name} onChange={(e) => setForm({...form, client_name: e.target.value})} placeholder="Jane Doe" data-testid="share-client-name" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Company</Label><Input value={form.client_company} onChange={(e) => setForm({...form, client_company: e.target.value})} placeholder="Acme Corp" data-testid="share-client-company" /></div>
                    <div><Label>Email</Label><Input value={form.client_email} onChange={(e) => setForm({...form, client_email: e.target.value})} placeholder="jane@acme.com" data-testid="share-client-email" /></div>
                  </div>
                  <div>
                    <Label>Channel</Label>
                    <Select value={form.channel} onValueChange={(v) => setForm({...form, channel: v})}>
                      <SelectTrigger data-testid="share-channel"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="email">Email</SelectItem>
                        <SelectItem value="link">Direct link</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="sms">SMS</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="What did you tell them?" data-testid="share-notes" /></div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button className="btn-primary" onClick={submit} data-testid="share-submit-btn">Record share</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {shares.length === 0 ? (
          <p className="text-sm text-neutral-500 italic">Not floated yet. Record every client this candidate has been shared with — you'll never forget who's seen them.</p>
        ) : (
          <div className="space-y-2">
            {shares.map(s => {
              const v = s.feedback?.verdict;
              const vStyle = v ? VERDICT_STYLES[v] : null;
              return (
                <div key={s.share_id} className="border rounded-lg p-3 flex items-start justify-between gap-3" data-testid={`share-${s.share_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{s.client_name}</span>
                      {s.client_company && <span className="text-xs text-neutral-500">· {s.client_company}</span>}
                      <Badge variant="outline" className="text-[9px] uppercase tracking-widest">{s.channel}</Badge>
                      {vStyle && <Badge className={`${vStyle.cls} border text-[9px] uppercase tracking-widest`}><vStyle.Icon className="w-3 h-3 mr-0.5" /> {v}</Badge>}
                    </div>
                    <div className="text-xs text-neutral-500 mt-1">
                      {s.client_email && <span>{s.client_email} · </span>}
                      Floated {new Date(s.shared_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })} by {s.shared_by_name}
                    </div>
                    {s.notes && <div className="text-xs text-neutral-600 mt-1 italic">"{s.notes}"</div>}
                    {s.feedback?.comment && <div className="text-xs text-neutral-700 mt-1 bg-neutral-50 rounded p-1.5">Client: "{s.feedback.comment}"</div>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!v && (
                      <>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setFeedback(s.share_id, "INTERESTED")} data-testid={`share-interested-${s.share_id}`} title="Interested"><ThumbsUp className="w-3.5 h-3.5 text-teal" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setFeedback(s.share_id, "MAYBE")} title="Maybe"><HelpCircle className="w-3.5 h-3.5 text-amber-500" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setFeedback(s.share_id, "PASS")} title="Pass"><ThumbsDown className="w-3.5 h-3.5 text-red-500" /></Button>
                      </>
                    )}
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => del(s.share_id)} title="Delete"><Trash2 className="w-3.5 h-3.5 text-neutral-400" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ==================== Outreach (SMS + WhatsApp) — MOCKED ====================

export function OutreachDialog({ candidateId, candidateName }) {
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState("sms");
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [templates, setTemplates] = useState([]);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/outreach/templates").then(r => setTemplates(r.data)).catch(() => {});
    api.get(`/outreach/log?candidate_id=${candidateId}`).then(r => setLog(r.data)).catch(() => {});
  }, [open, candidateId]);

  const applyTemplate = (t) => {
    setChannel(t.channel);
    setMessage(t.body.replace("{name}", candidateName || "there").replace("{company}", "Yousolved"));
  };

  const send = async () => {
    if (!to || !message) { toast.error("Number + message required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/outreach/send", { channel, to, message, candidate_id: candidateId });
      toast.success(data.note || "MOCK sent");
      setMessage("");
      api.get(`/outreach/log?candidate_id=${candidateId}`).then(r => setLog(r.data));
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Send failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full" data-testid="outreach-btn"><MessageSquare className="w-3.5 h-3.5 mr-1" /> SMS / WhatsApp</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Message {candidateName}</DialogTitle>
          <DialogDescription>Reach the candidate on their phone — via SMS or WhatsApp Business.</DialogDescription>
        </DialogHeader>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2 text-xs text-amber-900" data-testid="outreach-mock-banner">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <div><strong>MOCKED integration.</strong> This logs the outreach locally but no real SMS/WhatsApp is sent. Add Twilio credentials to backend/.env to enable real delivery.</div>
        </div>

        {templates.length > 0 && (
          <div>
            <Label className="text-xs">Templates</Label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {templates.map(t => (
                <button key={t.key} onClick={() => applyTemplate(t)} className="text-xs px-2.5 py-1 rounded-full border hover:bg-neutral-50" data-testid={`outreach-tpl-${t.key}`}>
                  {t.label} <span className="text-neutral-400">· {t.channel.toUpperCase()}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Channel</Label>
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger data-testid="outreach-channel"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sms"><Phone className="w-3.5 h-3.5 inline mr-1.5" /> SMS</SelectItem>
                <SelectItem value="whatsapp"><MessageCircle className="w-3.5 h-3.5 inline mr-1.5" /> WhatsApp</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>To (E.164)</Label><Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="+61412345678" data-testid="outreach-to" /></div>
        </div>
        <div><Label>Message</Label><Textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value.slice(0, 1600))} data-testid="outreach-message" />
          <div className="text-[10px] text-neutral-400 text-right mt-1">{message.length}/1600</div>
        </div>

        {log.length > 0 && (
          <div className="border-t pt-3">
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Recent messages (mock log)</div>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {log.slice(0, 5).map(l => (
                <div key={l.outreach_id} className="text-xs bg-neutral-50 rounded p-2 flex items-start gap-2">
                  <Badge variant="outline" className="text-[9px] uppercase tracking-widest">{l.channel}</Badge>
                  <div className="min-w-0 flex-1"><div className="text-neutral-800 truncate">{l.message}</div><div className="text-neutral-400 mt-0.5">to {l.to} · {new Date(l.created_at).toLocaleString()}</div></div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Close</Button>
          <Button className="btn-primary" onClick={send} disabled={busy} data-testid="outreach-send-btn">{busy ? "Sending…" : `Send (MOCK ${channel.toUpperCase()})`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
